import React, { useEffect, useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { getSession } from 'next-auth/react';
import { closePopup } from '@solidstarters/solid-core-ui/dist/redux/features/popupSlice';
import { useDispatch } from 'react-redux';

const API_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

interface TrackFilters {
  line?: string;
  location?: string;
  type?: string;
  size?: string;
  transactionType?: string;
  bondName?:string
}

interface ReportParams {
  reportType: string;
  bondId?: string;
  filters?: TrackFilters;
}


const reportOptions = [
  { label: 'Track Report', value: 'Track Report' },
  { label: 'Pending Containers 120 days', value: 'Pending Containers 120 days' },
  { label: 'Pending Containers 135 days', value: 'Pending Containers 135 days' },
  { label: 'Pending Containers 160 days', value: 'Pending Containers 160 days' },
  { label: 'Pending Containers 180 days', value: 'Pending Containers 180 days' },
  { label: 'Vessel Report', value: 'Vessel Report' },
  { label: 'Re-Export Report Containers', value: 'Re-Export Report Containers' },
  { label: 'Re-Export Report', value: 'Re-Export Report' },
  { label: 'Lg Report', value: 'Lg Report' },
];



const transactionTypeOptions = [
  { label: 'Import', value: 'Import' },
  { label: 'Export', value: 'Export' },
  { label: 'Extension', value: 'Extension' },
];

const sizeOptions = [
  { label: '20', value: '20' },
  { label: '40', value: '40' },
  { label: '45', value: '45' },
];

const typeOptions = [
  { label: 'Gen', value: 'Gen' },
  { label: 'Ref', value: 'Ref' },
  { label: 'Tank', value: 'Tank' },
];







const validationSchema = Yup.object({
  reportType: Yup.string().required('Report type is required'),
  bond: Yup.string().test(
    'bond-required',
    'Bond is required for this report type',
    function (value) {
      const { reportType } = this.parent;  // Access parent values, i.e., `reportType`
      if (['Re-Export Report', 'Re-Export Report Containers', 'Lg Report'].includes(reportType)) {
        return value && value.trim().length > 0 ? true : false;  // Ensure bond is provided
      }
      return true;  // Don't apply validation if reportType is not one of the above
    }
  ),
});


const GenerateReport = ({ context }: any) => {
  const dispatch = useDispatch()
  const [bondOptions, setBondOptions] = useState<any[]>([]);
  const [lineOptions, setLineOptions] = useState<any[]>([]);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const toast = useRef<Toast>(null);

  const fetchBondData = async () => {
    try {
      const session: any = await getSession();
      const token = session?.user?.accessToken || '';
      const response = await axios.get(`${API_URL}/api/bond`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const bonds = response.data?.data?.records || [];
      setBondOptions(
        bonds.map((bond: any) => ({
          label: bond.bondNumber,
          value: bond.id,
        }))
      );
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch bond data',
        life: 3000,
      });
      console.error('Bond fetch error:', err);
    }
  };

  const fetchLineData = async () => {
    try {
      const session: any = await getSession();
      const token = session?.user?.accessToken || '';
      const res = await axios.get(`${API_URL}/api/line-master`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLineOptions(res.data?.data?.records.map((l: any) => ({ label: l.name, value: l.name })) || []);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch lines', life: 3000 });
      console.error('Line fetch error:', err);
    }
  };

  const fetchLocationData = async () => {
    try {
      const session: any = await getSession();
      const token = session?.user?.accessToken || '';
      const res = await axios.get(`${API_URL}/api/location-master`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLocationOptions(res.data?.data?.records.map((loc: any) => ({ label: loc.name, value: loc.name })) || []);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch locations', life: 3000 });
      console.error('Location fetch error:', err);
    }
  };

  const handleGenerateReport = async (params: ReportParams) => {
    const { reportType, bondId, filters } = params;
      try {
      const session: any = await getSession();
      const token = session?.user?.accessToken || '';
      
      const response = await axios.get(`${API_URL}/api/generate-reports`, {
        params: {
          reportType,
          bondId,
          filters: JSON.stringify(filters), 
        },
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
     
      

      const disposition = response.headers['content-disposition'];
      const filename = disposition?.split('filename=')[1]?.replace(/"/g, '') || 'report.xlsx';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.current?.show({
        severity: 'success',
        summary: 'Report Downloaded',
        detail: filename,
        life: 3000,
      });
    } catch (error) {
      console.error('Report generation error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to generate report',
        life: 3000,
      });
    }
  };

  return (
    <div className="mt-10 px-4 bg-white rounded-xl shadow-lg border border-gray-200">
      <Toast ref={toast} />
      <Formik
        initialValues={{
          reportType: '',   bond: '' , line: '', location: '', type: '', size: '', transactionType: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const filters = {
            line: values.line || undefined,
            location: values.location || undefined,
            type: values.type || undefined,
            size: values.size || undefined,
            transactionType: values.transactionType || undefined,
            bondName: bondOptions?.find(bond => bond.value === values.bond)?.label
          };
        
          const params: ReportParams = {
            reportType: values.reportType,
            bondId: ['Re-Export Report','Re-Export Report Containers','Lg Report','Track Report'].includes(values.reportType)
              ? values.bond
              : undefined,
            filters,
          };
        
          await handleGenerateReport(params);
          setSubmitting(false);
        }}
        
      >
        {({ values, setFieldValue, isSubmitting }) => {
          useEffect(() => {
            if (values.reportType === 'Track Report') {
              fetchBondData();
              fetchLineData();
              fetchLocationData();
            } else if (
              ['Re-Export Report', 'Re-Export Report Containers', 'Lg Report'].includes(values.reportType)
            ) {
              fetchBondData();
            }
          }, [values.reportType]);

          return (
            <Form className="space-y-2 min-w-[400px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end mt-5 ">

                <h4 className="text-center w-full text-blue-900">Generate Reports</h4>
                <div className='w-full'>
                  <label htmlFor="reportType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Report Type
                  </label>
                  <Dropdown
                    id="reportType"
                    options={reportOptions}
                    value={values.reportType}
                    onChange={(e) => setFieldValue('reportType', e.value)}
                    placeholder="Select report type"
                    className="w-full p-inputtext-sm"
                  />
                  <ErrorMessage name="reportType" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                {(values.reportType === 'Re-Export Report' || values.reportType === 'Re-Export Report Containers' || values.reportType === 'Lg Report' || values.reportType === "Track Report") && (
                  <div className='w-full'>
                    <label htmlFor="bond" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Bond
                    </label>
                    <Dropdown
                      id="bond"
                      options={bondOptions}
                      value={values.bond}
                      onChange={(e) => setFieldValue('bond', e.value)}
                      placeholder="Select a bond"
                      className="w-full p-inputtext-sm"
                    />
                    <ErrorMessage name="bond" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                )}

                {(values.reportType === 'Track Report') && (
                  <div className='w-full'>
                    <label htmlFor="line" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Line
                    </label>
                    <Dropdown
                      id="line"
                      options={lineOptions}
                      value={values.line}
                      onChange={(e) => setFieldValue('line', e.value)}
                      placeholder="Select a Line"
                      className="w-full p-inputtext-sm"
                      multiple
                       optionLabel="label"
                    />
                  </div>
                )}

                {(values.reportType === 'Track Report') && (
                  <div className='w-full'>
                    <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Location
                    </label>
                    <Dropdown
                      id="location"
                      options={locationOptions}
                      value={values.location}
                      onChange={(e) => setFieldValue('location', e.value)}
                      placeholder="Select a Location"
                      className="w-full p-inputtext-sm"
                    />
                  </div>
                )}

                {(values.reportType === 'Track Report') && (
                  <div className='w-full'>
                    <label htmlFor="size" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Size
                    </label>
                    <Dropdown
                      id="size"
                      options={sizeOptions}
                      value={values.size}
                      onChange={(e) => setFieldValue('size', e.value)}
                      placeholder="Select a Location"
                      className="w-full p-inputtext-sm"
                    />
                  </div>
                )}
                {(values.reportType === 'Track Report') && (
                  <div className='w-full'>
                    <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Type
                    </label>
                    <Dropdown
                      id="type"
                      options={typeOptions}
                      value={values.type}
                      onChange={(e) => setFieldValue('type', e.value)}
                      placeholder="Select a Location"
                      className="w-full p-inputtext-sm"
                    />
                  </div>
                )}

                {(values.reportType === 'Track Report') && (
                  <div className='w-full'>
                    <label htmlFor="transactionType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Transaction Type
                    </label>
                    <Dropdown
                      id="transactionType"
                      options={transactionTypeOptions}
                      value={values.transactionType}
                      onChange={(e) => setFieldValue('transactionType', e.value)}
                      placeholder="Select a Location"
                      className="w-full p-inputtext-sm"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 mt-6 py-3">
                <Button
                  label="Cancel"
                  className="p-button-secondary"
                  type="button"
                  onClick={() => dispatch(closePopup())}
                />
                <Button
                  label="Generate"
                  icon="pi pi-check"
                  type="submit"
                  loading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default GenerateReport;
